import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Card from "@amzn/meridian/card";
import Alert from "@amzn/meridian/alert";
import { createYardMap } from "store";
import { processGeoJSONFile } from "utils/geoJsonUtils";
import { useTranslation } from "react-i18next";
import { keys } from "../../i18n";

export default function MapUploadHandler({ yardId }) {
  const geoJsonFileInputRef = useRef(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const { creatingYardMap, errorCreatingYardMap } = useSelector((state) => state.yardMapData);
  const { permissions } = useSelector((state) => state.permissionsData) || {};

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const canEdit = !!(permissions && permissions.canEdit);

  const handleGeoJsonUpload = () => {
    geoJsonFileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    // store a reference to the input element
    const fileInput = event.target;
    const file = fileInput?.files?.[0];
    if (!file) {
      return;
    }

    setSelectedFileName(file.name);
    setValidationError(null);

    try {
      const fileContent = await file.text();
      const processedData = await processGeoJSONFile(fileContent);

      const yardMapData = {
        yardId,
        gpsPosition: processedData.anchorPoint,
        locations: processedData.locations,
      };

      dispatch(createYardMap(yardMapData));
    } catch (error) {
      // handling file-processing errors, API-related errors for map creation are handled separately
      if (error.name === "ValidationError") {
        setValidationError(error.message);
      } else {
        setValidationError({ errors: [{ message: error.message }] });
      }
    } finally {
      // reset the input so the same file can be selected again if needed
      fileInput.value = "";
    }
  };

  return (
    <div>
      <Card>
        <Column alignmentHorizontal="center" spacing="large">
          <Text variant="heading2" weight="bold">
            {t(keys.IMPORT_YARD_MAP)}
          </Text>

          {validationError && (
            <Alert type="error" width="100%">
              <div>
                <div>{t(keys.ERROR_VALIDATING_YARD_MAP)}</div>
                {validationError.errors.map((error, index) => {
                  const featureRef =
                    error.featureIndex !== null ? ` (Feature #${error.featureIndex + 1})` : "";
                  return (
                    <div key={index}>
                      {index + 1}) {error.message}
                      {featureRef}
                    </div>
                  );
                })}
              </div>
            </Alert>
          )}

          {errorCreatingYardMap && (
            <Alert type="error">
              {`${t(keys.ERROR_CREATING_YARD_MAP)}: ${errorCreatingYardMap}`}
            </Alert>
          )}

          <Row spacingHorizontal="medium" alignmentHorizontal="center">
            <input
              type="file"
              ref={geoJsonFileInputRef}
              onChange={(e) => handleFileChange(e)}
              accept=".json,.geojson,application/json,application/geo+json"
              className="hidden"
              data-testid="geo-json-file-input"
            />
            <Button
              onClick={handleGeoJsonUpload}
              disabled={creatingYardMap || !canEdit}
              size="large"
              variant="primary"
            >
              {creatingYardMap ? t(keys.CREATING) : t(keys.IMPORT_GEO_JSON)}
            </Button>
          </Row>

          {selectedFileName && (
            <Text variant="body">
              {t(keys.SELECTED_FILE)}: {selectedFileName}
            </Text>
          )}
        </Column>
      </Card>
    </div>
  );
}

MapUploadHandler.propTypes = {
  yardId: PropTypes.string.isRequired,
};
